import React from 'react'
import { Button, ButtonProps, CircularProgress } from '@mui/material'
import withCapabilities from '../../hoc/withCapabilities'
import theme from '../../theme'

/**
 * ActionButton Component
 *
 * A customizable button component with predefined styles for different actions and sizes.
 *
 * Available button types:
 * - 'primary' (default): Primary action button (contained, primary color)
 * - 'approve': Approval button (outlined, success color)
 * - 'reject': Rejection button (outlined, error color)
 * - 'secondary': Secondary action button (contained, secondary color)
 *
 * Available sizes:
 * - 'small': Small-sized button
 * - 'medium': Medium-sized button
 * - 'large': Large-sized button
 * - 'fullWidth' (default): Full-width button
 *
 * Usage:
 * <ActionButton buttonType="approve" size="medium">Approve</ActionButton>
 */

interface ActionButtonProps extends Omit<ButtonProps, 'size'> {
  buttonType?: 'primary' | 'approve' | 'reject' | 'secondary'
  size?: 'small' | 'medium' | 'large' | 'fullWidth'
  isLoading?: boolean
}

const ActionButton: React.FC<ActionButtonProps> = ({
  children,
  buttonType = 'action',
  size: buttonSize = 'fullWidth',
  isLoading = false,
  ...props
}) => {
  let variant: 'contained' | 'outlined'
  let color: 'primary' | 'secondary' | 'error' | 'success'
  let backgroundColor = undefined
  switch (buttonType) {
    case 'approve':
      variant = 'outlined'
      color = 'success'
      break
    case 'reject':
      variant = 'outlined'
      color = 'error'
      break
    case 'secondary':
      variant = 'outlined'
      color = 'secondary'
      backgroundColor = theme.palette.background.light
      break
    default: //primary
      variant = 'contained'
      color = 'primary'
  }

  const buttonProps = {
    variant,
    color,
    ...props,
    ...(buttonSize === 'fullWidth' ? { style: { width: '100%' } } : { size: buttonSize }),
    ...(backgroundColor
      ? {
          sx: {
            backgroundColor,
            ...props.sx,
          },
        }
      : {}),
  }

  return (
    <Button {...buttonProps} sx={{ ...buttonProps.sx, textTransform: 'uppercase' }}>
      {children} {isLoading ? <CircularProgress size={24} sx={{ marginLeft: 2, color: 'text.greyOut' }} /> : null}
    </Button>
  )
}

export default withCapabilities(ActionButton)
